import { Injectable } from "@angular/core";

import { Observable } from "rxjs";
import { withLatestFrom, map, filter, distinctUntilChanged } from "rxjs/operators";

import { ApiResourceService } from "../api-resource.service";
import {
  OrganizationCustomization,
  ApiOrganizationCustomization,
  apiOrganizationCustomizationToOrganizationCustomization as clean
} from "./organization-customization";
import { ApiSessionService } from "../../api-session.service";
import { ApiAction } from "../../api-actions";
import { ApiStateStoreService } from "../../api-state-store.service";

import { sessionId } from "../../apiParams/session-id";
import { organizationId } from "../../apiParams/organization-id";
import { ParameterValue } from "../../util/api-action-description";
import { OnsipApiResponse, extractData } from "../../apiResponse/response-body-new";
import { getApiActionName } from "../../onsip-api-action-new";
import { arrayToRecord } from "../../util/arrayToRecord";
import { ApiPromiseState, ApiPromiseStateService } from "../../api-promise-state.service";
export { OrganizationCustomization };

const debug = false;

@Injectable({ providedIn: "root" })
export class OrganizationCustomizationService extends ApiResourceService<OrganizationCustomization> {
  constructor(
    session: ApiSessionService,
    store: ApiStateStoreService,
    promiseState: ApiPromiseStateService
  ) {
    super(session, store, promiseState, "OrganizationCustomization", "organizationId");
    debug && this.state.subscribe(state => console.warn("OrganizationCustomizationService", state));

    // call organizationCustomizationRead if there is a change in parentUserId aka a super user swapping to another user
    session.state
      .pipe(
        distinctUntilChanged(
          (a, b) => a.sessionId === b.sessionId && a.parentUserId === b.parentUserId
        )
      )
      .subscribe(state => {
        if (state.loggedIn && !!state.sessionId) this.organizationCustomizationRead();
        else if (!state.loggedIn) this.dispose();
      });
  }

  /** public getter to get a filtered observable with just the self organization */
  get organization(): Observable<OrganizationCustomization> {
    return this.state.pipe(
      withLatestFrom(this.store.state.pipe(organizationId())),
      map(([substate, selfOrganizationId]) => substate.state[selfOrganizationId]),
      filter(<T>(organization: T): organization is NonNullable<T> => !!organization)
    );
  }

  organizationCustomizationRead(
    extraParameters?: Record<string, ParameterValue>
  ): ApiPromiseState<OrganizationCustomization> {
    this.dispatcher.next({
      parameters: {
        Action: ApiAction.OrganizationCustomizationRead,
        SessionId: this.store.state.pipe(sessionId()),
        OrganizationId: this.store.state.pipe(organizationId()),
        ...extraParameters
      }
    });
    return this.promiseState.toPromise(ApiAction.OrganizationCustomizationRead);
  }

  organizationCustomizationEdit({
    accentColor,
    blurb,
    greeting
  }: {
    accentColor?: string;
    blurb?: string;
    greeting?: string;
  }): ApiPromiseState<OrganizationCustomization> {
    const givenParams: any = {};
    if (accentColor) givenParams["TeamPageInfo[AccentColor]"] = accentColor;
    if (blurb) givenParams["TeamPageInfo[Blurb]"] = blurb;
    if (greeting) givenParams["TeamPageInfo[Greeting]"] = greeting;
    this.dispatcher.next({
      parameters: {
        Action: ApiAction.OrganizationCustomizationEdit,
        SessionId: this.store.state.pipe(sessionId()),
        OrganizationId: this.store.state.pipe(organizationId()),
        ...givenParams
      }
    });
    return this.promiseState.toPromise(ApiAction.OrganizationCustomizationEdit);
  }

  reducer(response: OnsipApiResponse): void {
    const action = getApiActionName(response);
    switch (action) {
      case ApiAction.OrganizationCustomizationRead:
      case ApiAction.OrganizationCustomizationEdit:
        this.store.mergeStateUpdate(
          this.resourceName,
          arrayToRecord(
            [
              extractData<ApiOrganizationCustomization>(
                response,
                action,
                "OrganizationCustomization"
              )
            ].map(clean),
            this.indexKeyName
          ),
          action
        );
        break;
    }
  }
}
